import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextAlign } from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { FontFamily } from "@tiptap/extension-font-family";
import { Underline } from "@tiptap/extension-underline";
import { Highlight } from "@tiptap/extension-highlight";
import { useEffect } from "react";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  List, ListOrdered, Paintbrush, Type, Eraser
} from "lucide-react";

const FONTS = [
  { label: "Arial", value: "Arial, sans-serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Times New Roman", value: "'Times New Roman', serif" },
  { label: "Verdana", value: "Verdana, sans-serif" },
  { label: "Tahoma", value: "Tahoma, sans-serif" },
  { label: "Trebuchet MS", value: "'Trebuchet MS', sans-serif" },
  { label: "Courier New", value: "'Courier New', monospace" },
  { label: "Impact", value: "Impact, sans-serif" },
  { label: "Palatino", value: "'Palatino Linotype', serif" },
  { label: "Comic Sans", value: "'Comic Sans MS', cursive" },
];

const COLORS = ["#000000", "#333333", "#666666", "#ffffff", "#e11d48", "#ea580c", "#ca8a04", "#16a34a", "#0ea5e9", "#7c3aed", "#db2777"];

function ToolbarButton({ onClick, active, children, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`p-1 rounded transition-colors ${active ? "bg-blue-100 text-blue-700" : "text-slate-600 hover:bg-slate-100"}`}
      title={title}
    >
      {children}
    </button>
  );
}

function Toolbar({ editor }) {
  if (!editor) return null;

  return (
    <div className="flex flex-wrap items-center gap-0.5 p-1.5 border border-b-0 border-slate-300 rounded-t-md bg-slate-50">
      {/* Font family */}
      <select
        value={editor.getAttributes("textStyle").fontFamily || ""}
        onChange={(e) => {
          if (e.target.value) editor.chain().focus().setFontFamily(e.target.value).run();
          else editor.chain().focus().unsetFontFamily().run();
        }}
        className="h-7 text-xs border border-slate-300 rounded px-1 bg-white max-w-[110px]"
        title="Tipo de letra"
      >
        <option value="">Fuente</option>
        {FONTS.map(f => <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>{f.label}</option>)}
      </select>

      <div className="w-px h-5 bg-slate-300 mx-0.5" />

      {/* Bold, Italic, Underline, Strike */}
      <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Negrita">
        <Bold className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Cursiva">
        <Italic className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Subrayado">
        <UnderlineIcon className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Tachado">
        <Strikethrough className="w-3.5 h-3.5" />
      </ToolbarButton>

      <div className="w-px h-5 bg-slate-300 mx-0.5" />

      {/* Text color */}
      <div className="relative group">
        <ToolbarButton title="Color de texto">
          <Type className="w-3.5 h-3.5" />
        </ToolbarButton>
        <div className="absolute top-full left-0 hidden group-hover:flex flex-wrap gap-0.5 p-1.5 bg-white border rounded shadow-lg z-50 w-[130px]">
          {COLORS.map(c => (
            <button key={c} type="button" onClick={() => editor.chain().focus().setColor(c).run()} className="w-5 h-5 rounded border border-slate-200 hover:scale-110 transition-transform" style={{ backgroundColor: c }} title={c} />
          ))}
          <button type="button" onClick={() => editor.chain().focus().unsetColor().run()} className="w-5 h-5 rounded border text-[8px] flex items-center justify-center hover:bg-slate-100" title="Quitar color">
            <Eraser className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Highlight */}
      <div className="relative group">
        <ToolbarButton title="Resaltar">
          <Paintbrush className="w-3.5 h-3.5" />
        </ToolbarButton>
        <div className="absolute top-full left-0 hidden group-hover:flex flex-wrap gap-0.5 p-1.5 bg-white border rounded shadow-lg z-50 w-[130px]">
          {["#fef08a", "#bbf7d0", "#bae6fd", "#e9d5ff", "#fecaca", "#fed7aa", "#ffffff"].map(c => (
            <button key={c} type="button" onClick={() => editor.chain().focus().toggleHighlight({ color: c }).run()} className="w-5 h-5 rounded border border-slate-200 hover:scale-110" style={{ backgroundColor: c }} />
          ))}
          <button type="button" onClick={() => editor.chain().focus().unsetHighlight().run()} className="w-5 h-5 rounded border text-[8px] flex items-center justify-center hover:bg-slate-100">
            <Eraser className="w-3 h-3" />
          </button>
        </div>
      </div>

      <div className="w-px h-5 bg-slate-300 mx-0.5" />

      {/* Alignment */}
      <ToolbarButton onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Izquierda">
        <AlignLeft className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Centrar">
        <AlignCenter className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Derecha">
        <AlignRight className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().setTextAlign("justify").run()} active={editor.isActive({ textAlign: "justify" })} title="Justificar">
        <AlignJustify className="w-3.5 h-3.5" />
      </ToolbarButton>

      <div className="w-px h-5 bg-slate-300 mx-0.5" />

      {/* Lists */}
      <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Lista">
        <List className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Lista numerada">
        <ListOrdered className="w-3.5 h-3.5" />
      </ToolbarButton>
    </div>
  );
}

export default function RichTextEditor({ value, onChange, placeholder }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      FontFamily,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Sync external value changes
  useEffect(() => {
    if (editor && value !== undefined && editor.getHTML() !== value) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  return (
    <div>
      <Toolbar editor={editor} />
      <div className="border border-slate-300 rounded-b-md">
        <EditorContent
          editor={editor}
          className="prose prose-sm max-w-none [&_.tiptap]:min-h-[100px] [&_.tiptap]:p-3 [&_.tiptap]:outline-none [&_.tiptap]:text-sm"
        />
      </div>
    </div>
  );
}
